import React, { useState, useEffect } from 'react';
import { checkGenerationStatus } from '../services/apiService';

const ResultViewer = ({ jobId, generationType }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setNotificationPermission('granted');
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  // Function to show notification
  const showNotification = (title, body) => {
    if (notificationPermission === 'granted') {
      new Notification(title, {
        body,
        icon: '/favicon.ico', // You might want to add a favicon to your public folder
        requireInteraction: true
      });
    }
  };

  useEffect(() => {
    let intervalId;
    let attempts = 0;
    const maxAttempts = 180; // Maximum number of status check attempts (15 minutes total)
    const checkInterval = 5000; // Check every 5 seconds

    const checkStatus = async () => {
      try {
        const statusData = await checkGenerationStatus(jobId, generationType);
        console.log('Status check result:', statusData);

        // Check if the job is complete based on the OpenAPI specification
        if (statusData.status === 'SUCCESSFUL' || statusData.status === 'completed') {
          // Extract video URL from the response based on the OpenAPI structure
          let videoUrl = null;

          if (statusData.response && statusData.response.data && statusData.response.data.result) {
            videoUrl = statusData.response.data.result;
          } else if (statusData.video_url) {
            videoUrl = statusData.video_url;
          } else if (statusData.result) {
            videoUrl = statusData.result;
          }

          if (videoUrl) {
            const newResult = {
              ...statusData,
              video_url: videoUrl,
              extended_prompt: statusData.response?.data?.extended_prompt || statusData.extended_prompt || '',
              timestamp: new Date().toISOString()
            };

            setResults(prevResults => {
              const isNewResult = prevResults.length === 0 ||
                prevResults[0].video_url !== newResult.video_url;

              if (isNewResult) {
                showNotification(
                  'Video Generation Complete',
                  'Your video is ready to view!'
                );
              }

              return [newResult, ...prevResults];
            });

            setLoading(false);
            clearInterval(intervalId); // Stop checking once complete
          } else {
            console.error('Job completed but no video URL found in response:', statusData);
            setError('Generation completed but no video URL was returned');
            setLoading(false);
            clearInterval(intervalId);
          }
        } else if (statusData.status === 'FAILED' ||
                  statusData.status === 'failed' ||
                  statusData.status === 'error' ||
                  statusData.status === 'ERROR') {
          setError(`Generation failed: ${statusData.message || 'Unknown error'}`);
          setLoading(false);
          clearInterval(intervalId);
        }

        // Stop checking after max attempts
        attempts++;
        if (attempts >= maxAttempts) {
          setError('Generation is taking too long. Please try again later. Timeout after 15 minutes.');
          setLoading(false);
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Error checking status:', err);
        setError(err.message || 'Failed to check generation status');
        setLoading(false);
        clearInterval(intervalId);
      }
    };

    // Initial check
    checkStatus();

    // Set up interval for subsequent checks
    intervalId = setInterval(checkStatus, checkInterval);

    // Clean up interval on component unmount
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [jobId, generationType, notificationPermission]);

  // Render loading state, error, or results
  if (loading) {
    return (
      <div className="result-viewer">
        <h2>Generating Video</h2>
        <div className="job-info">
          <p><strong>Job ID:</strong> {jobId}</p>
          <p><strong>Type:</strong> {generationType}</p>
        </div>
        <div className="loading-indicator">
          <p>This may take a few minutes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="result-viewer">
        <h2>Generation Error</h2>
        <div className="error-message">
          <p>{error}</p>
        </div>
        <div className="job-info">
          <p><strong>Job ID:</strong> {jobId}</p>
          <p><strong>Type:</strong> {generationType}</p>
        </div>
      </div>
    );
  }

  if (results.length > 0) {
    const visibleResults = showAll ? results : results.slice(0, 2);

    return (
      <div className="result-viewer">
        <h2>Generation Results</h2>
        <div className="results-container">
          {visibleResults.map((result, index) => (
            <div key={result.timestamp} className="result-item">
              <div className="video-container">
                <video
                  className="result-video"
                  controls
                  autoPlay={index === 0}
                  loop
                  src={result.video_url}
                >
                  Your browser does not support the video tag.
                </video>
              </div>

              {result.extended_prompt && (
                <div className="extended-prompt">
                  <h3>Extended Prompt:</h3>
                  <p>{result.extended_prompt}</p>
                </div>
              )}

              <div className="video-actions">
                <a
                  href={result.video_url}
                  download={`generated-video-${index + 1}.mp4`}
                  className="download-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Video
                </a>
              </div>
            </div>
          ))}

          {results.length > 2 && (
            <button
              className="toggle-results-button"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Show Less' : `Show All (${results.length})`}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="result-viewer">
      <h2>Waiting for Result</h2>
      <div className="error-message">
        <p>No video result found. This may be a processing error.</p>
      </div>
    </div>
  );
};

export default ResultViewer;
